/**
 * The built-in company roles (FR-RBAC-001), as the SPA sees them.
 *
 * This file used to hold the whole template — every role, with a hand-copied
 * `grants` array — on the theory that "each `grants` array matches
 * `SystemRole::grants()` byte-for-byte" and that its own docblock flagged as an
 * asymmetry worth reporting, because nothing checked it. It drifted, exactly
 * as predicted, and in two ways at once:
 *
 *   1. `SystemRole::grants()` returns only IDENTITY's keys. Every other module
 *      contributes its defaults for these roles through `PermissionRegistry`
 *      when it boots — that is the whole point of §7.1, so that adding HR does
 *      not edit SystemRole. A mirror of the enum therefore cannot see HR's 46
 *      grants, and the Owner's permission matrix showed 12 of 58 with every HR
 *      checkbox unticked — claiming a Company Owner cannot view an employee.
 *   2. HR added an eighth role, `employee`, and the mirror stayed at seven.
 *
 * So the grants now come from `GET /companies/{company}/system-roles`, which
 * serves `PermissionGate::systemRoleGrants()` — the same resolution the API
 * authorises with. What stays here is the role LIST (needed synchronously, for
 * counts and pickers) and nothing else.
 *
 * Names and descriptions are UI copy and live in `packages/i18n` under
 * `roles.systemRoles.*` and `roles.systemRoleDescriptions.*`, keyed by the enum
 * value. They were inline `{ en, ar }` literals here, which is a plain
 * violation of the repo's second hard invariant, and it meant a language switch
 * could not reach them.
 */

import type { components } from '@multaqa/api-client'
import { localizedText } from '@/lib/me'

type Scope = 'own' | 'department' | 'branch' | 'company'

/**
 * Every built-in role, in the order `SystemRole::cases()` declares them. The
 * backend is the authority; a mismatch here shows up as a role the pickers
 * cannot offer, which is why `systemRolesMatchApi` in the tests compares this
 * list against what the endpoint serves.
 */
export const SYSTEM_ROLE_KEYS = [
  'company_owner',
  'company_administrator',
  'accountant',
  'sales_representative',
  'purchasing_officer',
  'warehouse_keeper',
  'hr_officer',
  // Added with HR to carry the `own`-scoped self-service grants (HR design
  // OPEN-1). The old hand-written mirror never gained it.
  'employee',
] as const

export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number]

export function isSystemRoleKey(key: string): key is SystemRoleKey {
  return (SYSTEM_ROLE_KEYS as readonly string[]).includes(key)
}

/** i18n key for a built-in role's display name. */
export function systemRoleNameKey(key: string): string {
  return `roles.systemRoles.${key}`
}

/** i18n key for a built-in role's one-line description. */
export function systemRoleDescriptionKey(key: string): string {
  return `roles.systemRoleDescriptions.${key}`
}

/**
 * The role shape the matrix renders: an entity row + a set of grants.
 *
 * `name` and `description` are null for a built-in role — its copy is an i18n
 * key, not stored text, so there is no `{ en, ar }` to carry. Read either
 * through `roleDisplayName` / `roleDescription`, which resolve both kinds.
 */
export type RoleLike = {
  /** The role's stable identifier. A uuid for custom roles, an enum for built-ins. */
  id: string
  /** The machine handle; the API derives it for custom roles and the enum is the literal. */
  key: string
  name: components['schemas']['LocalizedString'] | null
  description: components['schemas']['LocalizedString'] | null
  permissions: { key: string; scope: Scope }[]
  /** A built-in role cannot be edited from this screen. The flag is the matrix's gate. */
  builtIn: boolean
}

type TranslateFn = (key: string) => string

/**
 * A role's name in the active language, whichever kind of role it is. A
 * built-in resolves from the locale files so switching language moves it; a
 * custom role resolves from its stored bilingual text.
 */
export function roleDisplayName(role: RoleLike, t: TranslateFn, locale: string): string {
  if (role.builtIn) return t(systemRoleNameKey(role.key))
  return localizedText(role.name, locale) || role.key
}

export function roleDescription(role: RoleLike, t: TranslateFn, locale: string): string {
  if (role.builtIn) return t(systemRoleDescriptionKey(role.key))
  return localizedText(role.description, locale)
}

/**
 * Build the built-in role list from what the API served, in the order this
 * file declares.
 *
 * A role the endpoint did NOT mention is left out (E-7). The endpoint omits a
 * template the group's plan has made pointless — a Sales Representative in an
 * HR-only tenant holds nothing but the right to see themselves — and offering
 * it teaches the reader that a role they cannot use is worth assigning.
 *
 * This is why the list is built by filtering rather than by mapping. The old
 * mapping gave an unmentioned role an empty grant set, on the argument that an
 * empty matrix row is visibly wrong while a fabricated one is not. That was the
 * right answer while the endpoint served every role; now an absence is a fact,
 * not a gap.
 *
 * Order stays a frontend fact and membership a backend one. `SYSTEM_ROLE_KEYS`
 * still decides the sequence, so a role never moves when a plan changes.
 */
export function buildSystemRoles(
  grantsByRole: Readonly<Record<string, { key: string; scope: Scope }[]>>,
): RoleLike[] {
  return SYSTEM_ROLE_KEYS.filter((key) => key in grantsByRole).map((key) => ({
    id: key,
    key,
    name: null,
    description: null,
    permissions: grantsByRole[key] ?? [],
    builtIn: true,
  }))
}
